import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import Message from 'view/shared/message';

import {
  OnboardingAnswers,
  OnboardingChart,
  OnboardingDocLink,
  OnboardingEnrollment,
  OnboardingQuestion,
} from './payoutsOnboarding.types';
import PayoutsOnboardingService from './payoutsOnboarding.service';

const POSTMAN_COLLECTION_FILENAME =
  'payouts-onboarding.postman_collection.json';

export const getOnboardingQuestions = createAsyncThunk<
  OnboardingQuestion[],
  void,
  { state: RootState }
>(
  'payoutsOnboarding/get-questions',
  async (p, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutsOnboardingService.getQuestions({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      // A missing onboarding endpoint (404) must not trigger the global
      // Errors.handle redirect to /404 — degrade to an empty questionnaire.
      const errorObj = error as ErrorObject;
      if (errorObj.response?.status !== 404) {
        Errors.handle(errorObj);
      }
      return rejectWithValue(errorObj);
    }
  },
);

export const getOnboardingEnrollment = createAsyncThunk<
  OnboardingEnrollment,
  // Polling calls pass { silent: true } so a transient failure does not toast.
  { silent?: boolean } | undefined,
  { state: RootState; rejectValue: { notFound: boolean } | ErrorObject }
>(
  'payoutsOnboarding/get-enrollment',
  async (options, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutsOnboardingService.getEnrollment({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      const errorObj = error as ErrorObject;

      if (errorObj.response?.status === 404) {
        return rejectWithValue({ notFound: true });
      }

      if (!options?.silent) {
        Errors.handle(errorObj);
      }
      return rejectWithValue(errorObj);
    }
  },
);

export const createOnboardingEnrollment = createAsyncThunk<
  OnboardingEnrollment,
  OnboardingAnswers,
  { state: RootState }
>(
  'payoutsOnboarding/create-enrollment',
  async (answers, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutsOnboardingService.createEnrollment({
        answers,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const getOnboardingAssets = createAsyncThunk<
  { charts: OnboardingChart[]; docLinks: OnboardingDocLink[] },
  void,
  { state: RootState }
>(
  'payoutsOnboarding/get-assets',
  async (p, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      const [charts, docLinks] = await Promise.all([
        PayoutsOnboardingService.getCharts({ appToken, appEnvironment }),
        PayoutsOnboardingService.getDocLinks({ appToken, appEnvironment }),
      ]);

      return { charts: charts ?? [], docLinks: docLinks ?? [] };
    } catch (error: unknown) {
      // Don't let a 404 on an unavailable assets endpoint redirect to /404.
      const errorObj = error as ErrorObject;
      if (errorObj.response?.status !== 404) {
        Errors.handle(errorObj);
      }
      return rejectWithValue(errorObj);
    }
  },
);

export const downloadPostmanCollection = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'payoutsOnboarding/download-postman-collection',
  async (p, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      const collection = await PayoutsOnboardingService.getPostmanCollection({
        appToken,
        appEnvironment,
      });

      const content =
        typeof collection === 'string'
          ? collection
          : JSON.stringify(collection, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = POSTMAN_COLLECTION_FILENAME;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      Message.error('Could not download the Postman collection');
      return rejectWithValue(error);
    }

    return undefined;
  },
);
